import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ClientEvent } from '../../constants/matrixEvents';
import { useMatrixClient } from '../../context/MatrixContext';
import { useModal } from '../../context/ModalContext';
import { useI18n } from '../../context/I18nContext';
import { useUserDirectorySearch } from '../../hooks/useUserDirectorySearch';
import SearchInput from '../SearchInput';
import logger from '../../utils/logger';
import './IgnoredUsersPage.css';

/**
 * Ignored Users Settings Page
 * 
 * Allows users to view and manage their list of ignored users.
 * - Add users to ignore list via search
 * - View currently ignored users
 * - Remove users from ignore list
 * 
 * Uses Matrix SDK's m.ignored_user_list account data
 */
const IgnoredUsersPage = ({ onBack }) => {
  const client = useMatrixClient();
  const { showError, showConfirm } = useModal();
  const { t } = useI18n();
  const { searchUsers, results, isSearching, clearResults } = useUserDirectorySearch();
  
  const [ignoredUsers, setIgnoredUsers] = useState([]);
  const [displayNames, setDisplayNames] = useState({}); // Cache for fetched display names
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Load ignored users list
  const loadIgnoredUsers = useCallback(() => {
    if (!client) return;
    
    try {
      const ignored = client.getIgnoredUsers() || [];
      setIgnoredUsers(ignored);
    } catch (error) {
      logger.error('Failed to load ignored users:', error);
      showError(t('IgnoredUsersPage.failedToLoadTitle'), error.message);
    }
  }, [client, showError, t]);
  
  // Load on mount
  useEffect(() => {
    loadIgnoredUsers();
  }, [loadIgnoredUsers]);
  
  // Fetch display names for ignored users
  useEffect(() => {
    if (!client || ignoredUsers.length === 0) return;
    
    const fetchDisplayNames = async () => {
      const names = {};
      
      for (const userId of ignoredUsers) {
        // Check if user is in memory first
        const user = client.getUser(userId);
        if (user?.displayName) {
          names[userId] = user.displayName;
        } else {
          // Fetch from server
          try {
            const profile = await client.getProfileInfo(userId);
            names[userId] = profile.displayname || userId;
          } catch (error) {
            logger.debug(`Failed to fetch profile for ${userId}:`, error);
            names[userId] = userId; // Fallback to user ID
          }
        }
      }
      
      setDisplayNames(names);
    };
    
    fetchDisplayNames();
  }, [client, ignoredUsers]);
  
  // Listen for ignore list changes from other sessions
  useEffect(() => {
    if (!client) return;

    const handleAccountData = (event) => {
      if (event.getType() === 'm.ignored_user_list') {
        logger.debug('Ignore list changed, reloading...');
        loadIgnoredUsers();
      }
    };

    client.on(ClientEvent.AccountData, handleAccountData);

    return () => {
      client.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [client, loadIgnoredUsers]);
  
  // Handle search input change
  const handleSearchChange = useCallback(async (value) => {
    setSearchTerm(value);
    
    if (value.trim().length >= 2) {
      try {
        await searchUsers(value, 10);
      } catch (error) {
        logger.error('Search error:', error);
      }
    } else {
      clearResults();
    }
  }, [searchUsers, clearResults]);
  
  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    clearResults();
  }, [clearResults]);
  
  // Add user to ignore list
  const handleIgnoreUser = useCallback(async (userId) => {
    if (!client) return;
    
    const myUserId = client.getUserId();
    if (myUserId === userId) {
      showError(t('IgnoredUsersPage.cannotIgnoreSelfTitle'), t('IgnoredUsersPage.cannotIgnoreSelfMessage'));
      return;
    }
    
    if (ignoredUsers.includes(userId)) {
      showError(t('IgnoredUsersPage.alreadyIgnoredTitle'), t('IgnoredUsersPage.alreadyIgnoredMessage'));
      return;
    }
    
    setIsLoading(true);
    try {
      await client.setIgnoredUsers([...ignoredUsers, userId]);
      setIgnoredUsers([...ignoredUsers, userId]);
      setSearchTerm('');
      clearResults();
      logger.info('Added user to ignore list:', userId);
    } catch (error) {
      logger.error('Failed to ignore user:', error);
      showError(t('IgnoredUsersPage.failedToIgnoreTitle'), error.message);
    } finally {
      setIsLoading(false);
    }
  }, [client, ignoredUsers, showError, clearResults, t]);
  
  // Remove user from ignore list
  const handleUnignoreUser = useCallback(async (userId) => {
    if (!client) return;
    
    const confirmed = await new Promise((resolve) => {
      showConfirm(
        t('IgnoredUsersPage.unignoreTitle'),
        t('IgnoredUsersPage.unignoreConfirmation', { userId }),
        () => resolve(true),
        () => resolve(false),
      );
    });
    
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      const newIgnoredUsers = ignoredUsers.filter(id => id !== userId);
      await client.setIgnoredUsers(newIgnoredUsers);
      setIgnoredUsers(newIgnoredUsers);
      logger.info('Removed user from ignore list:', userId);

      // Reload to pick up conversations created during the ignore period.
      // Synapse filters invites from ignored users during /sync and does NOT
      // retroactively deliver them after unignoring. A fresh page load forces
      // a full initial sync which discovers all current room memberships.
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      logger.error('Failed to unignore user:', error);
      showError(t('IgnoredUsersPage.failedToUnignoreTitle'), error.message);
    } finally {
      setIsLoading(false);
    }
  }, [client, ignoredUsers, showError, showConfirm, t]);
  
  // Filter search results to exclude already ignored users
  const filteredResults = results.filter(user => !ignoredUsers.includes(user.user_id));
  
  return (
    <div className="settings-page-container">
      <div className="settings-modal-header">
        <button className="back-button" onClick={onBack} aria-label={t('IgnoredUsersPage.backButtonLabel')}>
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('IgnoredUsersPage.title')}</span>
      </div>
      
      <div className="settings-page-content">
        <div className="ignored-users-search-section">
          <SearchInput
            value={searchTerm}
            onChange={handleSearchChange}
            onClearClick={handleClearSearch}
            placeholder={t('IgnoredUsersPage.searchPlaceholder')}
            autoFocus
          />
          
          <p className="ignored-users-info-text">
            {t('IgnoredUsersPage.infoText')}
          </p>
          
          {isSearching && (
            <div className="ignored-users-search-loading">
              <div className="spinner" />
              {t('IgnoredUsersPage.searching')}
            </div>
          )}
          
          {searchTerm && filteredResults.length > 0 && (
            <div className="ignored-users-search-results">
              {filteredResults.map(user => (
                <div
                  key={user.user_id}
                  className="ignored-users-search-result"
                  onClick={() => handleIgnoreUser(user.user_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleIgnoreUser(user.user_id);
                    }
                  }}
                >
                  <div className="ignored-users-search-result-info">
                    <div className="ignored-users-search-result-name">
                      {user.display_name || user.user_id}
                    </div>
                    <div className="ignored-users-search-result-id">
                      {user.user_id}
                    </div>
                  </div>
                  <ion-icon name="add-outline" class="ignored-users-add-icon" />
                </div>
              ))}
            </div>
          )}
          
          {searchTerm && !isSearching && filteredResults.length === 0 && results.length === 0 && (
            <div className="ignored-users-no-results">
              {t('IgnoredUsersPage.noResults', { searchTerm })}
            </div>
          )}
        </div>
        
        <div className="ignored-users-list-section">
          {ignoredUsers.length === 0 ? (
            <div className="ignored-users-empty">
              <ion-icon name="person-remove-outline" />
              <p>{t('IgnoredUsersPage.emptyTitle')}</p>
              <span>{t('IgnoredUsersPage.emptyDescription')}</span>
            </div>
          ) : (
            <div className="ignored-users-list">
              {(() => {
                // Find duplicate display names
                const displayNameCounts = {};
                ignoredUsers.forEach(userId => {
                  const displayName = displayNames[userId] || userId;
                  displayNameCounts[displayName] = (displayNameCounts[displayName] || 0) + 1;
                });
                
                return ignoredUsers.map(userId => {
                  const displayName = displayNames[userId] || userId;
                  const showId = displayNameCounts[displayName] > 1;
                  
                  return (
                    <div key={userId} className="ignored-user-item">
                      <div className="ignored-user-info">
                        <div className="ignored-user-name">
                          {displayName}{showId && ` (${userId})`}
                        </div>
                      </div>
                      <button
                        className="ignored-user-remove-button"
                        onClick={() => handleUnignoreUser(userId)}
                        disabled={isLoading}
                        title={t('IgnoredUsersPage.removeButtonTitle')}
                      >
                        <ion-icon name="trash-outline" />
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

IgnoredUsersPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default IgnoredUsersPage;
